import React from 'react';
import styled from 'styled-components';
import {Checkbox} from '@forecast-it/design-system';
import {chatFont, D, skyTokens} from './design-tokens';
import {KeyValueTable} from './KeyValueTable';
import {KeyValueForm} from './KeyValueForm';
import type {AffectedItem, EntityType, HistoryProp} from './actionCardTypes';
import Util from '../../forecast-app/shared/util/util';
import {EVENT_ID, dispatch} from '../../containers/event_manager';

// The 4px left bar is unconditional: the Figma "Confirmation" frame shows it on
// the SINGLE-item card too, which is what b729bba9f4 removed it from. Colour is
// sky/sys/color/border/active, pinned like the rest of the drawer. (TNSR-450)
const Row = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
	padding: 8px 16px;
	background: ${D.bg};
	border: 1px solid ${skyTokens.color.border.decorative};
	border-left: 4px solid ${skyTokens.color.border.active};
	border-radius: 4px;
`;

const Content = styled.div`
	flex: 1 0 0;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

// Figma sky/sys/font/subheading/2. Previously set size/weight but no family, so
// it fell back to the app's global neue-haas-grotesk-text.
const Title = styled.div`
	${skyTokens.font.subheading['2']};
	color: ${skyTokens.color.text.content.main};
`;

// Figma sky/sys/font/body/2 — same missing-family problem as Title.
const Desc = styled.div`
	${skyTokens.font.body['2']};
	color: ${skyTokens.color.text.content.main};
`;

const ViewLink = styled.button`
	border: none;
	background: none;
	cursor: pointer;
	color: ${skyTokens.color.text.content.primary};
	font-family: ${chatFont};
	font-size: 14px;
	font-weight: 500;
	padding: 0 4px;
	align-self: center;
`;

export function ActionItemRow({
	item,
	checked,
	disabled,
	onToggle,
	showView,
	onViewed,
	history,
	entityType,
	edits,
	onEdit,
	editsReadOnly,
	hideCheckbox,
	fieldOverrideErrors,
	createdTaskId,
}: {
	item: AffectedItem;
	checked: boolean;
	disabled: boolean;
	onToggle: (id: string) => void;
	showView?: boolean;
	/** Called when the user opens the result through the View link. */
	onViewed?: () => void;
	history?: HistoryProp;
	entityType?: EntityType;
	edits?: Record<string, unknown>;
	onEdit?: (entityId: string, fieldKey: string, value: unknown, typed?: boolean) => void;
	editsReadOnly?: boolean;
	hideCheckbox?: boolean;
	fieldOverrideErrors?: Record<string, string>;
	/**
	 * The companyTaskId this row created, on a task-create card. Undefined when the row
	 * is not on a task-create card; null when it is but the result maps no task to it.
	 */
	createdTaskId?: string | null;
}): JSX.Element {
	const kvRows = item.kv ? Object.entries(item.kv).map(([key, value]) => ({key, value})) : [];
	const viewUrl = item.view_url;
	// For a task, open the task modal overlaid on the current page via the canonical
	// helper (works from any page, swaps a task modal already open, refetches for fresh
	// state). `originalId` is the companyTaskId the modal keys on. We deliberately do NOT
	// use `view_url` for tasks — the backend emits a relative `card/{id}` path that matches
	// no app route. Non-task entities have no task modal, so fall back to view_url.
	// On a task-create card the row id is the task's position in the batch, not a
	// companyTaskId, so the modal opens the id the confirm created instead, and a row
	// with no created id cannot open one.
	const taskId = createdTaskId !== undefined ? createdTaskId : item.originalId ?? item.id;
	const canOpenTask = entityType === 'task' && taskId != null && /^\d+$/.test(String(taskId));
	const canView = !!history && (canOpenTask || !!viewUrl);
	const onView = (): void => {
		if (!history) return;
		onViewed?.();
		if (canOpenTask) {
			// Highlight + scroll the task on the board (if mounted) on View,
			// same as on plan approval — not only when the plan is confirmed.
			dispatch(EVENT_ID.HIGHLIGHT_TASKS, [String(taskId)]);
			Util.showTaskModal(taskId, history);
		} else if (viewUrl) {
			history.push(viewUrl);
		}
	};
	return (
		<Row data-testid="action-item-row">
			{!hideCheckbox && <Checkbox checked={checked} disabled={disabled} onChange={() => onToggle(item.id)} />}
			<Content>
				<Title>{item.title}</Title>
				<Desc>{item.description}</Desc>
				{kvRows.length > 0 && <KeyValueTable rows={kvRows} />}
				{item.fields && item.fields.length > 0 && (
					<KeyValueForm
						fields={item.fields}
						edits={edits ?? {}}
						onEdit={(k, ...commit) => onEdit && onEdit(item.id, k, ...commit)}
						readOnly={editsReadOnly}
						fieldOverrideErrors={fieldOverrideErrors}
					/>
				)}
			</Content>
			{showView && canView && (
				<ViewLink type="button" onClick={onView}>
					View
				</ViewLink>
			)}
		</Row>
	);
}
